import { LineLayer, PointLayer, PolygonLayer, RasterLayer } from '@antv/l7'
import type { ILayer, Scene } from '@antv/l7'
import { useEffect, useRef } from 'react'
import { circlePolygon, estimateIntensityRadiusKm } from '../lib/geo'
import { EMPTY_TRACKS, type TrackTask } from '../lib/track'
import { P_WAVE_KM_S, S_WAVE_KM_S, WAVE_MAX_RADIUS_KM, triggerPulseSize } from '../lib/seismic'
import { useTrackLayers } from './useTrackLayers'
import { useUiStore } from '../stores/ui'
import { useLiveStore } from '../stores/live'
import { useWaveStore } from '../stores/wave'
import type { DeviceDistItem, Earthquake } from '../api/schemas'

export type MapLayerData = {
  devices: DeviceDistItem[]
  earthquakes: Earthquake[]
  selectedEventId?: string
  focus?: { lat: number; lon: number; zoom?: number }
  /** 选中事件的救援任务志愿者轨迹（需 tasks.read，无权限/未选中为空数组） */
  tracks?: TrackTask[]
}

const deviceColor = (status: string) =>
  status === 'online' ? '#56d7ff' : status === 'low_battery' ? '#ffb020' : '#8b98ab'

const epicenterColor = (level: string) =>
  level === 'TAKE_ACTION' ? '#ff4d5e' : level === 'BE_AWARE' ? '#ffb020' : '#38bdf8'

type Layers = {
  basemap: ILayer
  labels: ILayer
  devices: ILayer
  epicenters: ILayer
  pulses: ILayer
  intensity: ILayer
  outline: ILayer
  livePulses: ILayer
  pFill: ILayer
  pLine: ILayer
  sFill: ILayer
  sLine: ILayer
}

const EMPTY_FC = { type: 'FeatureCollection' as const, features: [] }
const fc = (feature: ReturnType<typeof circlePolygon>) => ({
  type: 'FeatureCollection' as const,
  features: [feature],
})

/**
 * L7 图层管理：暗色瓦片底图 + 设备分布 + 震中标记 + 雷达脉冲 + 烈度圈
 * + 实时触发脉冲（MQTT trigger 直写）+ P/S 波传播环动画（alert 驱动）。
 * 数据变化走 layer.setData 增量更新，不重建 Scene。
 * 底图采用「普通图层 + rasterTile source」，Scene 内部自动接入瓦片调度。
 */
export function useMapLayers(
  sceneRef: { current: Scene | null },
  ready: boolean,
  data: MapLayerData,
  activeLayers: string[],
) {
  const layersRef = useRef<Layers | null>(null)
  const quakesRef = useRef<Earthquake[]>([])

  const liveTriggers = useLiveStore((s) => s.triggers)
  const liveDevices = useLiveStore((s) => s.devices)
  const wave = useWaveStore((s) => s.wave)

  // 志愿者救援轨迹：渐变折线 + 方向箭头，随「志愿者轨迹」图层开关显隐
  useTrackLayers(
    sceneRef,
    ready,
    activeLayers.includes('tracks') ? (data.tracks ?? EMPTY_TRACKS) : EMPTY_TRACKS,
    { zIndex: 7 },
  )

  // 点击地图 → 就近匹配震中并选中（要素拾取在部分环境下不稳定，坐标匹配更可靠）
  useEffect(() => {
    const scene = sceneRef.current
    if (!scene) return
    const onClick = (ev: { lngLat?: { lng?: number; lat?: number } }) => {
      const lng = ev?.lngLat?.lng
      const lat = ev?.lngLat?.lat
      if (lng == null || lat == null) return
      let best: Earthquake | null = null
      let bestDist = 0.4
      for (const q of quakesRef.current) {
        const dist = Math.hypot(q.epicenterLon - lng, (q.epicenterLat - lat) * 0.72)
        if (dist < bestDist) {
          bestDist = dist
          best = q
        }
      }
      if (best) useUiStore.getState().selectEvent(best.eventId)
    }
    scene.on('click', onClick)
    return () => {
      scene.off('click', onClick)
    }
  }, [sceneRef])

  // 初始化图层（ready 后一次）
  useEffect(() => {
    const scene = sceneRef.current
    if (!scene || !ready || layersRef.current) return

    const basemap = new RasterLayer({ zIndex: 0 }).source(
      // Esri World Dark Gray Base（免费无 key，注意 Esri 瓦片顺序为 z/y/x）
      'https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}',
      { parser: { type: 'rasterTile', tileSize: 256, zoomOffset: 0 } },
    )
    // 标注层：地名/行政边界，提升指挥图可读性
    const labels = new RasterLayer({ zIndex: 1 }).source(
      'https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Reference/MapServer/tile/{z}/{y}/{x}',
      { parser: { type: 'rasterTile', tileSize: 256, zoomOffset: 0 } },
    )

    // P/S 波传播环（fill + line 双层，rAF 驱动 setData 扩散）
    const pFill = new PolygonLayer({ zIndex: 3 })
      .source(EMPTY_FC)
      .color('#56d7ff')
      .style({ opacity: 0.05 })
    const pLine = new LineLayer({ zIndex: 3 })
      .source(EMPTY_FC)
      .shape('line')
      .size(1.5)
      .color('#56d7ff')
      .style({ opacity: 0.75 })
    const sFill = new PolygonLayer({ zIndex: 3 })
      .source(EMPTY_FC)
      .color('#ff4d5e')
      .style({ opacity: 0.07 })
    const sLine = new LineLayer({ zIndex: 3 })
      .source(EMPTY_FC)
      .shape('line')
      .size(2)
      .color('#ff4d5e')
      .style({ opacity: 0.9 })

    const intensity = new PolygonLayer({ zIndex: 2 })
      .source({ type: 'FeatureCollection', features: [] })
      .color('#ff4d5e')
      .style({ opacity: 0.07 })

    const outline = new LineLayer({ zIndex: 2 })
      .source({ type: 'FeatureCollection', features: [] })
      .shape('line')
      .size(1.5)
      .color('#ff4d5e')
      .style({ opacity: 0.9 })

    const pulses = new PointLayer({ zIndex: 3 })
      .source([], { parser: { type: 'json', x: 'lon', y: 'lat' } })
      .shape('radar')
      .size(40)
      .color('#ff4d5e')
      .animate({ enable: true, interval: 1, duration: 2 })
      .style({ opacity: 0.6 })

    const epicenters = new PointLayer({ zIndex: 4 })
      .source([], { parser: { type: 'json', x: 'lon', y: 'lat' } })
      .shape('triangle')
      .size('magnitude', (m: number) => Math.max(6, Math.min(16, 5 + m * 1.2)))
      .color('alertLevel', epicenterColor)
      .style({ stroke: '#070b12', strokeWidth: 1 })

    const devices = new PointLayer({ zIndex: 5 })
      .source([], { parser: { type: 'json', x: 'lon', y: 'lat' } })
      .shape('circle')
      .size(5)
      .color('status', deviceColor)
      .style({ opacity: 1, stroke: '#0b111c', strokeWidth: 1.5 })

    // 实时触发脉冲：MQTT trigger/+/+ 直写 live store，青色雷达波纹
    const livePulses = new PointLayer({ zIndex: 6 })
      .source([], { parser: { type: 'json', x: 'lon', y: 'lat' } })
      .shape('radar')
      .size('cav', (v: number) => triggerPulseSize(v))
      .color('#56d7ff')
      .animate({ enable: true, interval: 1, duration: 2 })
      .style({ opacity: 0.55 })

    layersRef.current = { basemap, labels, devices, epicenters, pulses, intensity, outline, livePulses, pFill, pLine, sFill, sLine }
    scene.addLayer(basemap)
    scene.addLayer(labels)
    scene.addLayer(pFill)
    scene.addLayer(pLine)
    scene.addLayer(sFill)
    scene.addLayer(sLine)
    scene.addLayer(intensity)
    scene.addLayer(outline)
    scene.addLayer(pulses)
    scene.addLayer(epicenters)
    scene.addLayer(devices)
    scene.addLayer(livePulses)
  }, [sceneRef, ready])

  // 数据更新
  useEffect(() => {
    const layers = layersRef.current
    if (!layers) return
    const { devices, earthquakes, selectedEventId } = data

    // HTTP 分布查询 + MQTT 心跳实时状态合并（分布查询无设备 id，直接追加）
    layers.devices.setData([
      ...devices.map((d) => ({ lat: d.lat, lon: d.lon, status: d.status })),
      ...Object.values(liveDevices).map((d) => ({ lat: d.lat, lon: d.lon, status: 'online' })),
    ])

    const active = earthquakes.filter((e) => e.status !== 'closed')
    quakesRef.current = active
    layers.epicenters.setData(
      active.map((e) => ({ lat: e.epicenterLat, lon: e.epicenterLon, magnitude: e.magnitude, alertLevel: e.alertLevel })),
    )

    const pulsing = active.filter((e) => (e.status === 'pending' || e.eventId === selectedEventId) && e.magnitude > 0)
    layers.pulses.setData(pulsing.map((e) => ({ lat: e.epicenterLat, lon: e.epicenterLon })))

    // 烈度圈：所有活跃震级事件
    const circles = {
      type: 'FeatureCollection' as const,
      features: active.filter((e) => e.magnitude > 0).map((e) =>
        circlePolygon(e.epicenterLon, e.epicenterLat, estimateIntensityRadiusKm(e.magnitude)),
      ),
    }
    layers.intensity.setData(circles)
    layers.outline.setData(circles)

  }, [data, liveDevices])

  // 实时触发脉冲：live store 变化即上屏
  useEffect(() => {
    const layers = layersRef.current
    if (!layers) return
    layers.livePulses.setData(liveTriggers.map((t) => ({ lat: t.lat, lon: t.lon, cav: t.cav })))
  }, [liveTriggers])

  // P/S 波传播环动画：rAF 驱动，约 10fps setData 控制开销
  useEffect(() => {
    const layers = layersRef.current
    if (!layers || !wave) return
    let raf = 0
    let lastSet = 0
    const tick = () => {
      raf = requestAnimationFrame(tick)
      const now = performance.now()
      if (now - lastSet < 100) return
      lastSet = now

      const elapsedS = Math.max(0, (Date.now() - wave.originTs) / 1000)
      const pR = Math.min(WAVE_MAX_RADIUS_KM, elapsedS * P_WAVE_KM_S)
      const sR = Math.min(WAVE_MAX_RADIUS_KM, elapsedS * S_WAVE_KM_S)
      const pCircle = pR > 1 ? circlePolygon(wave.lon, wave.lat, pR) : null
      const sCircle = sR > 1 ? circlePolygon(wave.lon, wave.lat, sR) : null
      layers.pFill.setData(pCircle ? fc(pCircle) : EMPTY_FC)
      layers.pLine.setData(pCircle ? fc(pCircle) : EMPTY_FC)
      layers.sFill.setData(sCircle ? fc(sCircle) : EMPTY_FC)
      layers.sLine.setData(sCircle ? fc(sCircle) : EMPTY_FC)

      // S 波扫出最大半径后结束动画
      if (sR >= WAVE_MAX_RADIUS_KM) useWaveStore.getState().stopWave()
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [wave])

  // 无波时清空传播环图层
  useEffect(() => {
    const layers = layersRef.current
    if (!layers || wave) return
    layers.pFill.setData(EMPTY_FC)
    layers.pLine.setData(EMPTY_FC)
    layers.sFill.setData(EMPTY_FC)
    layers.sLine.setData(EMPTY_FC)
  }, [wave])

  // live store 惰性过期清理（触发 5min / 设备 12min 窗口）
  useEffect(() => {
    const timer = setInterval(() => useLiveStore.getState().prune(), 10_000)
    return () => clearInterval(timer)
  }, [])

  // 图层显隐
  useEffect(() => {
    const layers = layersRef.current
    if (!layers) return
    layers.devices[activeLayers.includes('devices') ? 'show' : 'hide']()
    layers.epicenters[activeLayers.includes('epicenters') ? 'show' : 'hide']()
    layers.pulses[activeLayers.includes('epicenters') ? 'show' : 'hide']()
    layers.livePulses[activeLayers.includes('live') ? 'show' : 'hide']()
    const wavesOn = activeLayers.includes('waves')
    layers.pFill[wavesOn ? 'show' : 'hide']()
    layers.pLine[wavesOn ? 'show' : 'hide']()
    layers.sFill[wavesOn ? 'show' : 'hide']()
    layers.sLine[wavesOn ? 'show' : 'hide']()
    layers.intensity[activeLayers.includes('intensity') ? 'show' : 'hide']()
    layers.outline[activeLayers.includes('intensity') ? 'show' : 'hide']()
  }, [activeLayers])

  // 选中事件聚焦
  useEffect(() => {
    const scene = sceneRef.current
    if (!scene || !ready || !data.focus) return
    scene.setCenter([data.focus.lon, data.focus.lat])
    if (data.focus.zoom != null) scene.setZoom(data.focus.zoom)
  }, [sceneRef, ready, data.focus])
}
